/**
 * TenantContext — makes the active tenant explicit throughout the app.
 *
 * The backend acts on the tenant baked into the auth token
 * (custom:tenant_id / custom:tenant_slug), NOT on the subdomain the
 * user happens to be browsing. This context exposes:
 *  - the active tenant (id, slug, role from the token, display name
 *    enriched via GET /auth/v1/tenants), and
 *  - whether the URL subdomain diverges from the token's tenant, so
 *    the shell can warn the user they are not where they think they are.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { tenantApi, type TenantMembership } from '@/services/tenantApi';
import { getTenantSlug } from '@/utils/subdomain';

export interface ActiveTenant {
  tenantId: string;
  slug: string | null;
  /** Human-readable tenant name; falls back to the slug when unset. */
  displayName: string | null;
  role: string | null;
  /**
   * Resolved per-space content scopes ('*' = open-space baseline), from
   * GET /auth/v1/tenants. Null until shared-auth returns them — gate with
   * lib/grants helpers, which fail open on null (UX only; the backend is
   * the authority).
   */
  grants: Record<string, string[]> | null;
}

interface TenantContextValue {
  /** The tenant the backend is acting on (from the auth token). Null until known. */
  tenant: ActiveTenant | null;
  /** Slug parsed from the current hostname, if on a tenant subdomain. */
  urlSlug: string | null;
  /** True when the subdomain and the token's tenant disagree. */
  mismatch: boolean;
  /**
   * Best label to show in the UI: displayName, else slug, else the
   * user's name/email — never the raw tenant id.
   */
  label: string | null;
  /** All tenants the user belongs to (the active one flagged). */
  memberships: TenantMembership[];
  /** Refetch memberships, e.g. after a rename or a tenant switch. */
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  urlSlug: null,
  mismatch: false,
  label: null,
  memberships: [],
  refreshTenants: async () => {},
});

/**
 * Resolve the workspace label shown in the badge and document.title:
 * displayName → slug → the user's name/email. The raw tenant id is
 * deliberately not in the chain (#492).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function resolveTenantLabel(
  membership: Pick<TenantMembership, 'displayName' | 'slug'>,
  user: { fullName?: string; email?: string } | null,
): string {
  return membership.displayName || membership.slug || user?.fullName || user?.email || 'Workspace';
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuthContext();
  const [memberships, setMemberships] = useState<TenantMembership[]>([]);

  const refreshTenants = useCallback(async () => {
    try {
      setMemberships(await tenantApi.listTenants());
    } catch {
      // Non-fatal: the badge falls back to the slug from the token.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    tenantApi
      .listTenants()
      .then((tenants) => {
        if (!cancelled) setMemberships(tenants);
      })
      .catch(() => {
        // Non-fatal: the badge falls back to the slug from the token.
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const value = useMemo<TenantContextValue>(() => {
    // The active tenant comes from GET /auth/v1/tenants, whose `active`
    // flag reflects the token's tenant server-side. Token claims are not
    // used directly: the published shared-react-auth User type does not
    // expose the tenant custom claims yet.
    const active = memberships.find((m) => m.active);
    const urlSlug = getTenantSlug();
    if (!active) {
      return { tenant: null, urlSlug, mismatch: false, label: null, memberships, refreshTenants };
    }
    const slug = active.slug ?? null;
    // No kind is read from the id: ADR-127 deleted every meaning of an id
    // prefix, and Amendment 10 left one format (`org_`).
    const tenant: ActiveTenant = {
      tenantId: active.tenantId,
      slug,
      displayName: active.displayName || null,
      role: active.role ?? null,
      grants: active.grants ?? null,
    };
    return {
      tenant,
      urlSlug,
      mismatch: Boolean(urlSlug && slug && urlSlug !== slug),
      label: resolveTenantLabel(active, user),
      memberships,
      refreshTenants,
    };
  }, [memberships, user, refreshTenants]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant() {
  return useContext(TenantContext);
}
